<template>
  <main class="teacher-page">
    <div class="teacher-shell">
      <MarketingToolHeader
        eyebrow="Akademie Teacher"
        title="Teacher Dashboard"
        description="Behalte Lernfortschritt, Lizenzen und Team-Anmeldungen übersichtlich im Blick."
        icon="mdi-account-tie-outline">
        <template v-slot:actions>
          <v-btn icon class="icon-action academy-local-back" aria-label="Zurück zur Akademie" title="Zurück zur Akademie" to="/akademie">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="teacher-badge"><v-icon>mdi-account-check-outline</v-icon>Teacher-Bereich</span>
        </template>
      </MarketingToolHeader>

      <section class="metric-grid" aria-label="Teacher Übersicht">
        <v-card dark flat class="metric-card">
          <span class="metric-icon"><v-icon>mdi-account-group-outline</v-icon></span>
          <div>
            <span>Lernende</span><strong>{{ users.length }}</strong
            ><small>im Academy-Bereich</small>
          </div>
        </v-card>
        <v-card dark flat class="metric-card">
          <span class="metric-icon icon-blue"><v-icon>mdi-calendar-star-outline</v-icon></span>
          <div>
            <span>Schulungen</span><strong>{{ upcomingSessions.length }}</strong
            ><small>kommende Termine</small>
          </div>
        </v-card>
      </section>

      <v-card dark flat class="content-surface">
        <div class="surface-header">
          <div class="surface-title-group">
            <span class="surface-icon"><v-icon>mdi-calendar-check-outline</v-icon></span>
            <div>
              <div class="surface-eyebrow">Team-Planung</div>
              <h2>Schulungsmanagement</h2>
              <p>Prüfe die Belegung und verwalte Anmeldungen aus deinem Team.</p>
            </div>
          </div>
          <span class="surface-count">{{ upcomingSessions.length }} Termine</span>
        </div>

        <div v-if="loadingSessions" class="session-grid">
          <v-skeleton-loader v-for="index in 3" :key="index" dark type="article, actions" class="session-skeleton" />
        </div>
        <div v-else-if="upcomingSessions.length" class="session-grid">
          <article v-for="session in upcomingSessions" :key="session._id" class="session-card">
            <div class="session-top">
              <span class="date-tile"
                ><strong>{{ formatDateDay(session.startsAt) }}</strong
                ><small>{{ formatDateMonth(session.startsAt) }}</small></span
              >
              <span class="location-pill"><v-icon>mdi-map-marker-outline</v-icon>{{ session.location || "Ort offen" }}</span>
            </div>
            <h3>{{ session.title }}</h3>
            <div class="session-footer">
              <span
                ><v-icon>mdi-account-multiple-outline</v-icon><strong>{{ session.registeredCount || 0 }}</strong> /
                {{ session.maxParticipants }} Plätze</span
              >
              <v-btn color="#009640" depressed class="manage-action" @click="openTeamManager(session)">Team verwalten</v-btn>
            </div>
          </article>
        </div>
        <div v-else class="inline-empty">
          <v-icon>mdi-calendar-blank-outline</v-icon><span>Aktuell sind keine kommenden Schulungen geplant.</span>
        </div>
      </v-card>

      <v-card dark flat class="content-surface users-surface">
        <div class="surface-header">
          <div class="surface-title-group">
            <span class="surface-icon"><v-icon>mdi-account-school-outline</v-icon></span>
            <div>
              <div class="surface-eyebrow">Lernfortschritt</div>
              <h2>Mitarbeiter-Übersicht</h2>
              <p>Öffne eine Kartei für Details zu Modulen, Quizzes und Lizenzen.</p>
            </div>
          </div>
          <div class="surface-actions">
            <span class="surface-count">{{ filteredUsers.length }} Personen</span>
            <v-btn v-if="isAcademyAdmin" outlined color="#58d388" class="recalculate-action" @click="recalculateDialog = true">
              <v-icon left>mdi-calculator-variant-outline</v-icon>Neu berechnen
            </v-btn>
          </div>
        </div>

        <div class="filter-grid">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            label="Name oder Studio suchen"
            outlined
            hide-details
            color="#58d388"
            class="modern-field" />
          <v-select
            v-model="tierFilter"
            :items="['Alle', 'Bronze', 'Silber', 'Gold']"
            label="Höchste Stufe"
            outlined
            hide-details
            color="#58d388"
            class="modern-field" />
        </div>

        <div v-if="$vuetify.breakpoint.smAndDown" class="user-card-list">
          <button v-for="user in filteredUsers" :key="user._id || user.id" type="button" class="user-card" @click="openRecord(user)">
            <v-avatar size="40" color="#252826"
              ><span>{{ userInitials(user) }}</span></v-avatar
            >
            <div class="user-copy">
              <strong>{{ user.firstName }} {{ user.lastName }}</strong>
              <span>{{ user.studio || "Kein Studio" }} · {{ user.role || "Member" }}</span>
              <small class="user-progress-copy">{{ tierProgressLabel(user) }}</small>
              <v-progress-linear
                :value="currentTierProgress(user).percent"
                color="#009640"
                background-color="rgba(255,255,255,0.08)"
                height="6"
                rounded />
            </div>
            <span class="user-progress">{{ currentTierProgress(user).percent }}%</span>
            <v-icon class="user-arrow">mdi-chevron-right</v-icon>
          </button>
        </div>

        <div v-else class="table-scroll">
          <v-data-table
            :headers="headers"
            :items="filteredUsers"
            :loading="loading"
            :items-per-page="25"
            dark
            class="user-table"
            @click:row="openRecord">
            <template v-slot:item.name="{ item }">
              <div class="table-user">
                <v-avatar size="32" color="#252826"
                  ><span>{{ userInitials(item) }}</span></v-avatar
                >{{ item.firstName }} {{ item.lastName }}
              </div>
            </template>
            <template v-slot:item.highestTier="{ item }">
              <v-chip :color="getTierColor(item.highestTier)" small text-color="black" class="tier-chip">{{ item.highestTier || "Keine" }}</v-chip>
            </template>
            <template v-slot:item.progress="{ item }">
              <div class="table-progress">
                <div class="table-progress-copy">
                  <strong>{{ tierLabel(currentTierProgress(item).tier) }}</strong>
                  <small>{{ currentTierProgress(item).completed }} von {{ currentTierProgress(item).total }} Modulen</small>
                </div>
                <v-progress-linear :value="currentTierProgress(item).percent" color="#009640" rounded height="6" />
                <span>{{ currentTierProgress(item).percent }}%</span>
              </div>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon class="row-action" aria-label="Kartei öffnen" @click.stop="openRecord(item)"><v-icon>mdi-chevron-right</v-icon></v-btn>
            </template>
          </v-data-table>
        </div>
      </v-card>
    </div>

    <TeamRegistrationDialog ref="teamDialog" @refresh="loadSessions" />

    <v-dialog v-model="recalculateDialog" max-width="34rem" content-class="recalculate-dialog">
      <v-card dark class="recalculate-card">
        <div class="recalculate-heading">
          <span><v-icon>mdi-calculator-variant-outline</v-icon></span>
          <div>
            <small>Nur für Akademie-Admins</small>
            <h2>Fortschritt neu berechnen?</h2>
          </div>
        </div>
        <p>
          Alle Lizenzstufen werden anhand der aktuell aktiven Module und Präsenzpflichten neu geprüft. Bereits absolvierte Inhalte werden
          nicht gelöscht.
        </p>
        <div class="notification-note">
          <v-icon>mdi-bell-off-outline</v-icon>
          <span>Bei dieser manuellen Neuberechnung werden keine Push-Nachrichten und keine E-Mails versendet.</span>
        </div>
        <div class="recalculate-buttons">
          <v-btn text class="cancel-action" :disabled="recalculating" @click="recalculateDialog = false">Abbrechen</v-btn>
          <v-btn color="#009640" depressed class="confirm-action" :loading="recalculating" @click="recalculateProgress">
            Jetzt neu berechnen
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="feedback.show" :color="feedback.color" :timeout="5000" bottom>
      {{ feedback.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="feedback.show = false">Schliessen</v-btn>
      </template>
    </v-snackbar>
  </main>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import TeamRegistrationDialog from "./TeamRegistrationDialog.vue"
import MarketingToolHeader from "@/views/dashboard/communication/MarketingToolHeader.vue"

export default {
  name: "TeacherDashboard",
  components: { TeamRegistrationDialog, MarketingToolHeader },
  data() {
    return {
      users: [],
      upcomingSessions: [],
      loading: false,
      loadingSessions: false,
      recalculating: false,
      recalculateDialog: false,
      feedback: { show: false, text: "", color: "success" },
      search: "",
      tierFilter: "Alle",
      headers: [
        { text: "Name", value: "name" },
        { text: "Studio", value: "studio" },
        { text: "Rolle", value: "role" },
        { text: "Höchste Stufe", value: "highestTier" },
        { text: "Aktueller Lernweg", value: "progress" },
        { text: "", value: "actions", sortable: false, align: "right" },
      ],
    }
  },
  computed: {
    isAcademyAdmin() {
      return userService.hasRight("aka_manage")
    },
    filteredUsers() {
      let filtered = this.users
      if (this.tierFilter !== "Alle") {
        filtered = filtered.filter((user) => user.highestTier && user.highestTier.toLowerCase() === this.tierFilter.toLowerCase())
      }
      const query = this.search.trim().toLowerCase()
      if (!query) return filtered
      return filtered.filter((user) =>
        [user.firstName, user.lastName, user.studio, user.role].filter(Boolean).join(" ").toLowerCase().includes(query),
      )
    },
  },
  mounted() {
    this.loadUsers()
    this.loadSessions()
  },
  methods: {
    async loadUsers() {
      this.loading = true
      try {
        this.users = (await api.akademie.fetchTeacherUserList()) || []
      } catch (error) {
        console.error("Fehler beim Laden der Mitarbeiter-Liste:", error)
      } finally {
        this.loading = false
      }
    },
    async loadSessions() {
      this.loadingSessions = true
      try {
        const sessions = await api.akademie.fetchTermine()
        const now = new Date()
        this.upcomingSessions = (sessions || [])
          .filter((session) => new Date(session.startsAt) >= now)
          .sort((first, second) => new Date(first.startsAt) - new Date(second.startsAt))
      } catch (error) {
        console.error("Fehler beim Laden der Termine:", error)
      } finally {
        this.loadingSessions = false
      }
    },
    getTierColor(tier) {
      if (!tier) return "#a3a3a8"
      const colors = { bronze: "#d89557", silber: "#d4d4d8", gold: "#f4c95d" }
      return colors[tier.toLowerCase()] || "#a3a3a8"
    },
    tierLabel(tier) {
      const labels = { bronze: "Bronze", silber: "Silber", gold: "Gold" }
      return labels[(tier || "").toLowerCase()] || "Lernweg"
    },
    currentTierProgress(user) {
      const tiers = (user.tierProgress || []).filter((tier) => tier.total > 0)
      return tiers.find((tier) => tier.percent < 100) || tiers[tiers.length - 1] || {
        tier: "",
        completed: 0,
        total: 0,
        percent: Number(user.totalProgress) || 0,
      }
    },
    tierProgressLabel(user) {
      const progress = this.currentTierProgress(user)
      if (!progress.total) return "Noch keine aktiven Module"
      return `${this.tierLabel(progress.tier)} · ${progress.completed} von ${progress.total} Modulen`
    },
    async recalculateProgress() {
      if (!this.isAcademyAdmin || this.recalculating) return
      this.recalculating = true
      try {
        await api.akademie.recalculateTeacherProgress()
        await this.loadUsers()
        this.recalculateDialog = false
        this.feedback = {
          show: true,
          text: "Fortschritt und Lizenzstatus wurden ohne Benachrichtigungen neu berechnet.",
          color: "success",
        }
      } catch (error) {
        console.error("Fehler bei der manuellen Neuberechnung:", error)
        this.feedback = {
          show: true,
          text: (error.response && error.response.data && error.response.data.error) || "Die Neuberechnung ist fehlgeschlagen.",
          color: "error",
        }
      } finally {
        this.recalculating = false
      }
    },
    userInitials(user) {
      return `${user.firstName ? user.firstName.charAt(0) : ""}${user.lastName ? user.lastName.charAt(0) : ""}`
    },
    openRecord(user) {
      this.$router.push(`/akademie-teacher/user/${user._id || user.id}`)
    },
    openTeamManager(session) {
      if (this.$refs.teamDialog) this.$refs.teamDialog.open(session)
    },
    formatDateDay(date) {
      return date ? new Date(date).getDate() : ""
    },
    formatDateMonth(date) {
      if (!date) return ""
      return new Date(date).toLocaleDateString("de-CH", { month: "short" }).replace(".", "").toUpperCase()
    },
  },
}
</script>

<style scoped>
.teacher-page {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 1.5rem;
  color: var(--text);
  background: radial-gradient(circle at 15% -5%, rgba(0, 150, 64, 0.13), transparent 28rem), #121212;
  font-size: 1rem;
}

.teacher-shell {
  width: 100%;
  max-width: 92.5rem;
  margin: 0 auto;
}

.icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.teacher-badge {
  display: inline-flex;
  min-height: 2.75rem;
  align-items: center;
  gap: 0.4375rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.8125rem;
  color: #7dd3fc;
  background: rgba(14, 165, 233, 0.12);
  font-size: 0.875rem;
  font-weight: 750;
}

.teacher-badge .v-icon {
  color: currentColor;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 1rem 0;
}

.metric-card {
  display: flex;
  min-height: 6.75rem;
  align-items: center;
  gap: 0.875rem;
  padding: 1.125rem;
  border: 1px solid var(--border);
  border-radius: 1.1875rem !important;
  background: var(--surface) !important;
}

.metric-icon,
.surface-icon,
.toolbar-icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.metric-icon {
  width: 2.625rem;
  height: 2.625rem;
  border-radius: 0.8125rem;
}

.metric-icon.icon-blue {
  color: #7dd3fc;
  background: rgba(14, 165, 233, 0.11);
}

.metric-icon .v-icon,
.surface-icon .v-icon {
  color: currentColor;
}

.metric-card > div {
  display: grid;
}

.metric-card span {
  color: #d4d4d8;
  font-size: 0.875rem;
  font-weight: 700;
}

.metric-card strong {
  color: #ffffff;
  font-size: 1.6875rem;
  line-height: 1.15;
}

.metric-card small {
  color: var(--muted);
  font-size: 0.75rem;
}

.content-surface {
  margin-top: 1rem;
  padding: 1.375rem;
  border: 1px solid var(--border);
  border-radius: 1.375rem !important;
  background: rgba(255, 255, 255, 0.03) !important;
  box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.12);
}

.surface-header,
.surface-title-group,
.session-top,
.session-footer,
.table-user,
.table-progress {
  display: flex;
  align-items: center;
}

.surface-header {
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.surface-title-group {
  gap: 0.75rem;
}

.surface-icon {
  width: 2.625rem;
  height: 2.625rem;
  border-radius: 0.8125rem;
}

.surface-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.surface-header h2 {
  margin: 0.1875rem 0 0;
  color: var(--text);
  font-size: 1.25rem;
}

.surface-header p {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
}

.surface-count {
  flex: 0 0 auto;
  min-height: 1.75rem;
  padding: 0.3125rem 0.625rem;
  border: 1px solid var(--border);
  border-radius: 999px;
  color: #d4d4d8;
  background: rgba(255, 255, 255, 0.055);
  font-size: 0.75rem;
  font-weight: 700;
}

.surface-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.625rem;
}

.recalculate-action,
.confirm-action {
  min-height: 2.75rem !important;
  border-radius: 0.75rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.session-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.session-skeleton,
.session-card {
  min-height: 12rem;
  border: 1px solid var(--border);
  border-radius: 1rem;
  background: var(--surface) !important;
}

.session-card {
  display: flex;
  flex-direction: column;
  padding: 0.875rem;
}

.session-top,
.session-footer {
  justify-content: space-between;
  gap: 0.75rem;
}

.date-tile {
  display: grid;
  width: 3.25rem;
  min-height: 3.625rem;
  place-content: center;
  border-radius: 0.8125rem;
  color: #ffffff;
  background: rgba(0, 150, 64, 0.12);
  text-align: center;
}

.date-tile strong {
  font-size: 1.25rem;
  line-height: 1.05;
}

.date-tile small {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
}

.location-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.3125rem;
  padding: 0.3125rem 0.5rem;
  border-radius: 999px;
  color: #b8b8bc;
  background: rgba(255, 255, 255, 0.055);
  font-size: 0.75rem;
}

.location-pill .v-icon {
  color: #8f9095;
  font-size: 1rem;
}

.session-card h3 {
  flex: 1 1 auto;
  margin: 0.875rem 0;
  color: #ffffff;
  font-size: 0.9375rem;
  line-height: 1.4;
}

.session-footer {
  align-items: flex-end;
}

.session-footer > span {
  display: flex;
  align-items: center;
  gap: 0.3125rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.session-footer > span .v-icon {
  color: #8f9095;
  font-size: 1rem;
}

.session-footer > span strong {
  color: #ffffff;
  font-size: 0.875rem;
}

.manage-action {
  min-height: 2.75rem !important;
  border-radius: 0.75rem !important;
  color: #ffffff !important;
  font-size: 0.75rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.inline-empty {
  display: flex;
  min-height: 8rem;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 0.875rem;
  color: var(--muted);
  font-size: 0.875rem;
}

.inline-empty .v-icon {
  color: #8f9095;
}

.filter-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(14rem, 0.75fr);
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.modern-field ::v-deep .v-input__slot {
  min-height: 3rem !important;
  border-radius: 0.8125rem !important;
  background: rgba(255, 255, 255, 0.035) !important;
}

.modern-field ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.modern-field ::v-deep .v-label {
  font-size: 0.875rem;
}

.table-scroll {
  overflow-x: auto;
}

.user-table {
  min-width: 55rem;
  background: transparent !important;
}

.user-table ::v-deep tbody tr {
  cursor: pointer;
}

.user-table ::v-deep tbody tr:hover {
  background: rgba(255, 255, 255, 0.045) !important;
}

.table-user {
  gap: 0.625rem;
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 700;
}

.table-user .v-avatar span,
.user-card .v-avatar span {
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 750;
}

.tier-chip {
  min-height: 1.75rem;
  font-size: 0.75rem !important;
  font-weight: 750;
}

.table-progress {
  min-width: 17rem;
  gap: 0.625rem;
}

.table-progress .v-progress-linear {
  width: 5rem;
}

.table-progress-copy {
  display: grid;
  min-width: 7.75rem;
}

.table-progress-copy strong {
  color: #ffffff;
  font-size: 0.875rem;
}

.table-progress-copy small {
  color: var(--muted);
  font-size: 0.75rem;
}

.table-progress span {
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 750;
}

.row-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  color: #58d388 !important;
}

.user-card-list {
  display: grid;
  gap: 0.5rem;
}

.user-card {
  display: grid;
  min-height: 5rem;
  grid-template-columns: 2.5rem minmax(0, 1fr) auto 1.25rem;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.875rem;
  outline: none;
  color: #ffffff;
  background: var(--surface);
  text-align: left;
}

.user-card:focus-visible {
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.user-copy {
  display: grid;
  min-width: 0;
  gap: 0.1875rem;
}

.user-copy strong,
.user-copy > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-copy strong {
  font-size: 0.875rem;
}

.user-copy > span {
  color: var(--muted);
  font-size: 0.75rem;
}

.user-progress-copy {
  overflow: hidden;
  color: #c7c7cb;
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-progress {
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 750;
}

.user-arrow {
  color: #8f9095 !important;
}

.recalculate-card {
  padding: 1.5rem;
  border: 1px solid rgba(88, 211, 136, 0.28);
  border-radius: 1.25rem !important;
  background: #1b1d1c !important;
}

.recalculate-heading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.recalculate-heading > span {
  display: grid;
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 0.8125rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.14);
}

.recalculate-heading .v-icon {
  color: currentColor;
}

.recalculate-heading small {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.recalculate-heading h2 {
  margin: 0.125rem 0 0;
  color: #ffffff;
  font-size: 1.25rem;
}

.recalculate-card > p {
  margin: 1rem 0;
  color: #d4d4d8;
  font-size: 1rem;
  line-height: 1.55;
}

.notification-note {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.875rem;
  border: 1px solid rgba(125, 211, 252, 0.2);
  border-radius: 0.875rem;
  color: #ccecff;
  background: rgba(14, 165, 233, 0.09);
  font-size: 0.875rem;
  line-height: 1.45;
}

.notification-note .v-icon {
  flex: 0 0 auto;
  color: #7dd3fc;
}

.recalculate-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 0.625rem;
  margin-top: 1.25rem;
}

.cancel-action {
  min-height: 2.75rem !important;
  font-size: 0.875rem !important;
  letter-spacing: 0;
  text-transform: none;
}

.confirm-action {
  color: #ffffff !important;
}

@media (max-width: 78.9375rem) {
  .teacher-page {
    padding: 1.125rem;
  }

  .session-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 37.4375rem) {
  .teacher-page {
    padding: 0.75rem;
  }

  .metric-grid,
  .filter-grid,
  .session-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .metric-card {
    min-height: 5.75rem;
    padding: 0.875rem;
  }

  .content-surface {
    padding: 0.875rem;
    border-radius: 1.0625rem !important;
  }

  .surface-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .surface-actions {
    width: 100%;
    align-items: stretch;
    flex-direction: column;
    padding-left: 3.375rem;
  }

  .surface-count {
    align-self: flex-start;
    margin-left: 0;
  }

  .recalculate-action {
    width: 100%;
  }

  .session-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .manage-action {
    width: 100%;
  }

  .recalculate-card {
    padding: 1rem;
    border-radius: 1rem !important;
  }

  .recalculate-buttons {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .confirm-action,
  .cancel-action {
    width: 100%;
  }
}
</style>
